import { CONSTANTS_TIME__client } from '@/constants/CONSTANTS_TIME__client';
import { CONSTANTS__client } from '@/constants/CONSTANTS__client';
import { ENUM__html_ref_stub } from '@/enums/ENUM__html_ref_stub';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_ref_with_fallback } from '@/hooks/_base/use_ref_with_fallback';
import { use_slice_ui, use_slice_user } from '@/hooks/store/use_slices';
import { HOOK_GRID2D_FLOAT__update_possible_on_screen_cells_indices } from '@/pages/_base/grid2d/_hooks/HOOK_GRID2D_FLOAT__update_possible_on_screen_cells_indices';
import { HOOK_GRID2D__first_scroll_to } from '@/pages/_base/grid2d/_hooks/HOOK_GRID2D__first_scroll_to';
import { HOOK_GRID2D__float_virtualizer } from '@/pages/_base/grid2d/_hooks/HOOK_GRID2D__float_virtualizer';
import { HOOK_GRID2D__slug_offset } from '@/pages/_base/grid2d/_hooks/HOOK_GRID2D__slug_offset';
import { HOOK__get_div_size } from '@/pages/page_generation/_hooks/HOOK__get_div_size';
import { DTO__entity_with_grid2d } from '@shared/dto/+workspace/workspace_entity/_database_entity_with_grid2d_permissions/DTO__entity_with_grid2d';
import { DTO__grid2d } from '@shared/dto/grid2d/DTO__grid2d';
import { ENUM_UTILS__grid2d_cell_size } from '@shared/dto/grid2d_cell/ENUM__grid2d_cell_size';
import { DTO__grid2d_virtual } from '@shared/dto/grid2d_virtual/DTO__grid2d_virtual';
import { EShortcutType } from '@shared/dto/hint_shortcuts/e_shortcut_type';
import { throttle } from 'es-toolkit';
import { useMemo, useRef } from 'react';
import { GRID2D_CELL__float } from '../../grid2d_cell/views/GRID2D_CELL__float';
import { GRID2D_GALLERY__float } from '../../grid2d_gallery/GRID2D_GALLERY__float';
import { WATCHER_GRID2D_GALLERY__cell_current_update } from '../../grid2d_gallery/WATCHER_GRID2D_GALLERY__cell_current_set';
import { WATCHER__shortcut_grid2d_gallery_close } from '../../grid2d_sidebar_image_compare/_watchers/WATCHER__shortcut_grid2d_gallery_close';
import { ViewShortcutTrigger } from '../../shortcut_hint/ViewShortcutTrigger';
import { WATCHER_GRID2D_CHUNK__load_cells } from '../../watchers/grid2d/WATCHER_GRID2D_CHUNK__load_cells';
import { WATCHER_GRID2D__change_cell_size_by_scroll } from '../../watchers/grid2d/WATCHER_GRID2D__change_cell_size_by_scroll';
import { WatcherImageToTextTranslate } from '../../watchers/image_to_text/WatcherImageToTextTranslate';
import { DRAG_AND_DROP__grid2d_external } from '../DRAG_AND_DROP__grid2d_external';
import { DRAG_AND_DROP__grid2d_internal } from '../DRAG_AND_DROP__grid2d_internal';
import { GRID2D_BARS_bottom } from '../bars/GRID2D_BARS_bottom';
import { WATCHER_GRID2D_FLOAT__slug } from './WATCHER_GRID2D_FLOAT__slug';

interface PROPS {
  grid2d: DTO__grid2d;
  class_name_grid2d?: string;
  entity: DTO__entity_with_grid2d;
  use_offset_from_slug?: boolean;
}

export function GRID2D__float({ grid2d, entity, class_name_grid2d = 'overflow-auto', use_offset_from_slug = true }: PROPS) {
  /*
   * STORE
   */

  const { global__grid2d_show_as_gallery } = use_slice_ui(['global__grid2d_show_as_gallery']);
  const { user_settings } = use_slice_user(['user_settings']);
  const { ui_grid2d_cells_sort_newest_first } = user_settings;

  /*
   * STATE
   */
  const ref_grid2d_container = use_ref_with_fallback(ENUM__html_ref_stub.DIV);
  // const ref_grid = use_ref_with_fallback(ENUM__html_ref_stub.DIV);

  const { ref_element__set: div_grid__set, div_element: div_grid, size: div_row1d_size } = HOOK__get_div_size();

  const ref_first_image_index_on_page = useRef(0);

  const ref_is_slug_offset_locked_by_scroll = useRef(false);

  const ref_virtualizer_size = useRef(0);

  const max_side = useMemo(() => ENUM_UTILS__grid2d_cell_size.size_to_max_side(user_settings.ui_grid2d_cell_size), [user_settings.ui_grid2d_cell_size]);

  const ref_grid2d_virtual_previous = useRef<DTO__grid2d_virtual>(DTO__grid2d_virtual.CREATE_STUB());

  const grid2d_virtual_config = useMemo(() => {
    let total_columns = get_total_columns();

    return {
      total_columns,
      visible_cells_amount: DTO__grid2d_virtual.get_visible_cells_amount(div_row1d_size.height, total_columns, max_side, grid2d),
    };
  }, [max_side, grid2d, div_row1d_size]);

  const { slug_offset, set_slug_offset, set_slug_offset_from_url } = HOOK_GRID2D__slug_offset(
    use_offset_from_slug ? grid2d : DTO__grid2d.CREATE_STUB(),
    ref_first_image_index_on_page,
  );

  const grid2d_virtual: DTO__grid2d_virtual = useMemo(
    () => get_grid2d_virtual(),
    [slug_offset, grid2d, ui_grid2d_cells_sort_newest_first, grid2d_virtual_config],
  );

  const { virtualizer, try_recalculate_item_heights } = HOOK_GRID2D__float_virtualizer(
    grid2d_virtual,
    grid2d,
    div_grid,
    ui_grid2d_cells_sort_newest_first,
    max_side,
  );

  const grid2d_float_update_possible_on_screen_cells_indices = HOOK_GRID2D_FLOAT__update_possible_on_screen_cells_indices(
    div_grid,
    grid2d_virtual,
    virtualizer,
    ref_first_image_index_on_page,
    slug_offset,
    ref_is_slug_offset_locked_by_scroll,
  );

  const { is_first_scroll_done, set_is_first_scroll_done } = HOOK_GRID2D__first_scroll_to(
    grid2d_virtual,
    slug_offset,
    virtualizer,
    grid2d_float_update_possible_on_screen_cells_indices,
    true,
    max_side,
    ref_is_slug_offset_locked_by_scroll,
  );

  /*
   * EVENTS
   */

  /*
   * EFFECTS COMMON
   */

  use_effect(() => {
    const throttle_scroll_handler = throttle(
      grid2d_float_update_possible_on_screen_cells_indices,
      CONSTANTS_TIME__client.THROTTLE_GRID2D_FLOAT_UPDATE_POSSIBLE_ON_SCREEN_CELLS_INDICES,
    );

    div_grid.addEventListener('scroll', throttle_scroll_handler);

    return () => div_grid.removeEventListener('scroll', throttle_scroll_handler);
  }, [div_grid, grid2d_float_update_possible_on_screen_cells_indices]);

  use_effect(() => {
    ref_virtualizer_size.current = virtualizer.getTotalSize();
  }, []);

  use_effect(() => {
    if (slug_offset === ref_first_image_index_on_page.current) return;

    ref_first_image_index_on_page.current = slug_offset;
  }, [slug_offset]);

  use_effect(
    () => grid2d_float_update_possible_on_screen_cells_indices(),
    [div_row1d_size, grid2d_virtual, ui_grid2d_cells_sort_newest_first, grid2d_float_update_possible_on_screen_cells_indices],
  );

  /*
   * EFFECTS
   */
  use_effect(() => {
    set_slug_offset_from_url();

    set_is_first_scroll_done(false);
  }, []);

  /*
   * METHODS
   */

  function get_grid2d_virtual() {
    if (grid2d_virtual_config.total_columns === 0) return DTO__grid2d_virtual.CREATE_STUB(); // grid2d_virtual and virtualizer properties are not calculated yet based on viewport size

    const out = new DTO__grid2d_virtual();

    out.grid2d = grid2d.clone();
    out.cells__amount__grid2d_total = grid2d.total_cells_amount;

    out.total_columns = grid2d_virtual_config.total_columns;
    out.cells__amount__visible = grid2d_virtual_config.visible_cells_amount;
    out.cell__to_load_index_absolute = slug_offset;
    out.ui_grid2d_cells_sort_newest_first = ui_grid2d_cells_sort_newest_first;
    out.virtualizer_size = ref_virtualizer_size.current;

    out.init(ref_grid2d_virtual_previous.current);

    ref_grid2d_virtual_previous.current = out;

    return out;
  }

  function get_total_columns() {
    let out = CONSTANTS__client.GRID2D_VIRTUAL_TOTAL_COLUMNS_DEFAULT;
    if (div_row1d_size.is_zero()) return out;

    out = Math.max(Math.floor(div_row1d_size.width / (max_side + CONSTANTS__client.FLOAT_GRID_GAP)), 1);

    return out;
  }

  /*
   * RENDERS
   */

  const rendered_grid_cells = useMemo(() => {
    try_recalculate_item_heights();

    const virtual_items = virtualizer.getVirtualItems();
    const items = virtual_items.length > 0 ? virtual_items : [CONSTANTS__client.VIRTUAL_ITEM_STUB];

    return items.map(item => {
      const is_stub = item.index === CONSTANTS__client.VIRTUAL_ITEM_STUB_INDEX;
      const key_id = is_stub ? `${grid2d._id}-first-cell` : `${grid2d._id}-${item.index}`;

      const left = (item.lane ?? 0) * (max_side + CONSTANTS__client.FLOAT_GRID_GAP);
      const width = max_side;
      let height = is_stub ? max_side : item.size;

      return (
        <div
          id={key_id}
          key={key_id}
          style={{
            position: 'absolute',
            top: 0,
            left: `${left}px`,
            width: `${width + CONSTANTS__client.FLOAT_GRID_GAP}px`,
            height: `${height}px`,
            transform: `translateY(${item.start}px)`,
          }}
        >
          <GRID2D_CELL__float
            index={item.index}
            {...{ width, height, grid2d, entity, grid2d_virtual, ui_grid2d_cells_sort_newest_first, use_offset_from_slug }}
          />
        </div>
      );
    });
  }, [max_side, grid2d, entity, grid2d_virtual, ui_grid2d_cells_sort_newest_first, try_recalculate_item_heights]);

  function render_optional_watchers() {
    if (!use_offset_from_slug) return null;

    return <WATCHER_GRID2D_FLOAT__slug {...{ is_first_scroll_done, grid2d, grid2d_virtual, set_slug_offset, ref_first_image_index_on_page, slug_offset }} />;
  }

  function render_grid2d() {
    if (global__grid2d_show_as_gallery) return <GRID2D_GALLERY__float {...{ grid2d }} />;

    return (
      <div ref={div_grid__set} className={`info_grid_2d_container size-full flex flex-col justify-start  bg-component ${class_name_grid2d}`}>
        <div
          style={{
            position: 'relative',
            height: `${virtualizer.getTotalSize()}px`,
            width: `calc(${grid2d_virtual.total_columns * (max_side + CONSTANTS__client.FLOAT_GRID_GAP)}px - 2px)`,
          }}
        >
          {rendered_grid_cells}
        </div>
      </div>
    );
  }

  /*
   * HTML
   */

  return (
    <>
      <WATCHER__shortcut_grid2d_gallery_close />
      <WATCHER_GRID2D_CHUNK__load_cells {...{ entity, grid2d_virtual, grid2d }} />
      <WatcherImageToTextTranslate {...{ grid2d, entity }} />
      <WATCHER_GRID2D__change_cell_size_by_scroll {...{ div_grid }} />

      <WATCHER_GRID2D_GALLERY__cell_current_update {...{ grid2d }} />

      {render_optional_watchers()}

      <ViewShortcutTrigger hint_shortcut_keys={[EShortcutType.GRID2D_CHANGE_CELL_SIZE_WITH_SCROLL]}>
        <div className="size-full relative">
          {render_grid2d()}

          <GRID2D_BARS_bottom {...{ div_grid, grid2d, entity, grid2d_virtual }} />
          <DRAG_AND_DROP__grid2d_external {...{ grid2d, entity }} />
          <DRAG_AND_DROP__grid2d_internal {...{ grid2d, entity }} />
        </div>
      </ViewShortcutTrigger>
    </>
  );
}
